import React from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { ActionMenu, CommonCellRenderers } from "@netapp/bxp-design-system-react";
import { fleetManagement as i18nNamespace } from "i18n/namespace";
import { ONTAP_STORAGE_OBJECTS } from 'utils/fleet/fleetInventoryUtils';

export const getSystemColumns = (t, fleets = []) => [
    {
        header: t("used_capacity"),
        accessor: "used_capacity",
        id: "used_capacity",
        width: 175,
        sort: { enabled: false },
        Renderer: ({ row }) => {
            // The systems API returns space.capacityTotal / capacityAvailable for ONTAP, E-Series and StorageGRID
            const totalCapacity = _.get(row, 'space.capacityTotal');
            const availableCapacity = _.get(row, 'space.capacityAvailable');
            if (!_.isFinite(totalCapacity) || !_.isFinite(availableCapacity)) {
                return t("not_available");
            }
            const usedCapacity = totalCapacity - availableCapacity;
            if (usedCapacity < 0) {
                return t("not_available");
            }
            return CommonCellRenderers.CapacityRenderer({ value: usedCapacity });
        }
    },
    {
        header: t("connected_fleets"),
        accessor: "connected_fleets",
        id: "connected_fleets",
        sort: { enabled: false },
        width: 410,
        Renderer: ({ row }) => {
            const fleetIds = _.get(row, 'fleetIds', []);

            if (!fleetIds || fleetIds.length === 0) {
                return '-';
            }

            const fleetNames = fleetIds
                .map(fleetId => {
                    const fleet = fleets.find(f => f.id === fleetId);
                    return fleet?.name;
                })
                .filter(Boolean);

            return fleetNames.length > 0 ? fleetNames.join(', ') : '-';
        }
    }
];

export const MenuItems = ({ onItemClick, hasNoSystems }) => {
    const { t } = useTranslation(i18nNamespace);

    const noSystemsTooltip = hasNoSystems ? t("no_systems_available_to_add_volume_or_lun") : undefined;

    const items = [
        { displayName: t("LUNs"), id: ONTAP_STORAGE_OBJECTS.LUNS, disabled: hasNoSystems, tooltip: noSystemsTooltip },
        { displayName: t("System"), id: ONTAP_STORAGE_OBJECTS.SYSTEMS, disabled: false },
        { displayName: t("Volume"), id: ONTAP_STORAGE_OBJECTS.VOLUMES, disabled: hasNoSystems, tooltip: noSystemsTooltip }
    ];

    return items.map((item) => (
        <ActionMenu.Button key={item.id} onClick={() => onItemClick(item)} isDisabled={item.disabled} tooltip={item.tooltip}>
            {item.displayName}
        </ActionMenu.Button>
    ));
};

MenuItems.propTypes = {
    onItemClick: PropTypes.func.isRequired,
    hasNoSystems: PropTypes.bool
};

export const getSelectedResources = (selectedRows) => {
  const selectedResources = [];
  for (const selectedRow of Object.keys(selectedRows)) {
    if (selectedRows[selectedRow]) {
      selectedResources.push(selectedRow);
    }
  }
  return selectedResources;
}

export const formatTableData = (apiResponse) => {
    const results = apiResponse?.data;
    const items = _.get(results, 'items', []);
    if (_.isNil(items) || !Array.isArray(items)) {
        return { items: [], count: 0 };
    }
    const mappedItems = items?.map((item, index) => ({
        ...item,
        id: item.workingEnvironmentId || item.uuid || item.name || `row-${index}`,
    }));
    return {
        items: mappedItems,
        count: results?.count || mappedItems?.length || 0
    };
};

// Creates table config for InventoryTable with system selection
export const createSystemsTableConfig = (storageObjectConfig, onSelectionChange, t, fleets = []) => { 
    const viewConfig = storageObjectConfig.supportedViews["capacity"];

    const selectedColumns = [
        viewConfig.columns[0],
        viewConfig.columns[1],
        viewConfig.columns[2],
        ...getSystemColumns(t, fleets)
    ];

    return {
        columns: selectedColumns || [],
        pluralLabel: storageObjectConfig.pluralLabel,
        singularLabel: storageObjectConfig.singularLabel,
        hasSearch: storageObjectConfig.hasSearch ?? true,
        hasSelect: true,
        hasSelectAllRows: true,
        hasSorting: storageObjectConfig.hasSorting ?? false,
        hasRowSelecting: true,
        filterOptions: storageObjectConfig.filterOptions,
        searchPlaceholder: storageObjectConfig.searchPlaceholder,
        isViewSupported: !!viewConfig,
        defaultColumnState: storageObjectConfig.hiddenColumns?.["capacity"] || {},
        isTogglingColumns: storageObjectConfig.isTogglingColumnsSupported ?? false,
        selectionConfig: {
            isRowSelecting: true,
            isMultipleSelection: true,
            isSelectAllPagesEnabled: false,
            isSelectCurrentPageEnabled: true,
            isSelectionHeaderDisabled: false
        },
        onSelectionChange
    };
};

// Filter IAM resource hierarchy based on search query
export const IAMresourceHierarchyFilter = (treeData, searchQuery) => {
    if (!searchQuery) return treeData;

    const lowerQuery = _.toLower(searchQuery);

    const matchScope = (scope) => {
        const isMatch = _.toLower(scope.name).includes(lowerQuery);
        const matchedChildren = (scope.childScopes || []).map(matchScope).filter(Boolean);
        if (!isMatch && !matchedChildren.length) return null;
        return { ...scope, childScopes: isMatch ? (scope.childScopes || []) : matchedChildren };
    };

    return treeData.map(matchScope).filter(Boolean);
};

export const buildRefreshKey = (fleetId, storageObjectType) => `${fleetId || ""}:${storageObjectType || ""}`;

export const ALL_FLEETS_ID = "all";

export const isAllFleetsSelected = (fleetId) => fleetId === ALL_FLEETS_ID;
